/**
 * 提醒中心**中文文案唯一源** + 结构化字段 → 文本（H 域 · 端侧）
 *
 * 依据：原型 `docs/03-UX与UI设计/09-提醒中心_原型.html`（三档分组 · 逐条文案 · 通道卡）·
 * H 域 PRD **§5.1.4**（每条二选一：给动作 or 明确「无需处理」）· **§5.3.1**（三档名）·
 * **§7.1 `AC-H1.11`** · `DR-036`（金额文本走共享层单一源）· 铁律 **38**（UI 一律「笔」）。
 *
 * ## 🔴 端侧零口径（本文件一个数都不算）
 *
 * 服务端下发**结构化字段**（`type` / `variant` / `subject` / `tailNumber` / `dueDate` /
 * `daysUntil` / `amount` / `mergedCount` / `tag` / `group` / `state`），**文案全部在这里拼**。
 * ⇒ 服务端不出现中文展示文案，端侧不出现任何判定 —— 与 K 域报表同一分工
 * （`contract/report.ts` 出结构 · `features/report-labels.ts` 出文案）。
 *
 * ⚠️ **不做「未就绪」的假文案**：微信通道未接就照实说「尚未接入」，
 * 不写"即将上线"式的空话（同 ㊱ 批「不画点不动的控件」的纪律）。
 */

import {
  ReminderGroup,
  ReminderLevel,
  ReminderState,
  ReminderType,
  formatMoneyText,
  type Cents,
  type ReminderView,
} from '@jiaying/shared';

// ─────────────────────────── 页面与分组 ───────────────────────────

export const PAGE_TITLE = '提醒中心';
export const PAGE_SUBTITLE = '只在你需要的时候提醒你。站内始终可用，不依赖任何授权。';

/** 三档分组的组名（原型 `data-anchor` 原文） */
export const GROUP_LABELS: Record<ReminderGroup, string> = {
  [ReminderGroup.Action]: '需要你处理',
  [ReminderGroup.Info]: '知道就好',
  [ReminderGroup.Earlier]: '更早',
};

/** 关键 / 常规（`tag`）—— 端侧只按它上色，**不自判档位** */
export const TAG_LABELS = { key: '关键', normal: '常规' } as const;

/** 五类提醒的显示名（H 域 PRD §5.1.1） */
export const TYPE_LABELS: Record<ReminderType, string> = {
  [ReminderType.Repayment]: '还款日提醒',
  [ReminderType.Billing]: '信用卡账单日',
  [ReminderType.Budget]: '预算预警',
  [ReminderType.Goal]: '目标进度',
  [ReminderType.Habit]: '记账习惯',
};

/** 强度三档的显示名（H 域 PRD §5.3.1 · **只读展示**，改在 N 域设置页） */
export const LEVEL_LABELS: Record<ReminderLevel, string> = {
  [ReminderLevel.KeyOnly]: '只推关键',
  [ReminderLevel.Normal]: '常规',
  [ReminderLevel.All]: '全部',
};

// ─────────────────────────── 单条文案 ───────────────────────────

/** 🔴 **每条提醒二选一**（`AC-H1.11`）：有动作给动作；没动作**必须**明确说「无需处理」 */
export const NO_ACTION_TEXT = '无需处理，只是想让你知道';
export const ACTION_LABELS: Record<ReminderType, string> = {
  [ReminderType.Repayment]: '立即还款',
  [ReminderType.Billing]: '看账单明细',
  [ReminderType.Budget]: '看看是什么推高了',
  [ReminderType.Goal]: '去看看目标',
  [ReminderType.Habit]: '去补记',
};

/** 完成动作（`POST /:id/done`）的按钮文案 —— 原型「已还，别再提」 */
export const DONE_LABEL = '已还，别再提';
/** 「标记已读」按钮文案（只改状态，**不删行**） */
export const READ_LABEL = '知道了';

/** 状态徽标（只给「更早」档用；未读不显示徽标，靠加粗区分） */
export const STATE_LABELS: Record<ReminderState, string | null> = {
  [ReminderState.Unread]: null,
  [ReminderState.Read]: '已读',
  [ReminderState.Done]: '已处理',
  [ReminderState.Dismissed]: '已忽略',
};

// ─────────────────────────── 页面级文案 ───────────────────────────

export const MARK_ALL_READ = '全部标记已读';
export const EMPTY_TEXT = '目前没有需要提醒的事。';
export const EMPTY_EARLIER_TEXT = '还没有「更早」的历史提醒。';
export const READ_FAILED = '标记已读失败：';
export const DONE_FAILED = '标记已处理失败：';

/** 顶部计数卡 */
export const PENDING_TITLE = '待处理提醒';
export const PENDING_UNIT = '条';
export const KEY_SUFFIX = '条关键';
export const NORMAL_SUFFIX = '条常规';

/** 强度 / 免打扰（**只读** —— 写入口在 N 域设置页） */
export const LEVEL_TITLE = '提醒强度';
export const LEVEL_READONLY_NOTE = '改强度、按类型开关与免打扰时段，都在「设置」里。';
export const DND_TITLE = '免打扰时段';
export const DND_ACTIVE_NOTE = '免打扰中：只静默累积到本页，不发任何通知。';
export const DND_IDLE_NOTE = '免打扰外：按你的强度设置推送。';

/** 通道卡（🔴 **如实呈现** —— 站内恒可用、微信本批未接） */
export const CHANNEL_TITLE = '推送通道';
export const CHANNEL_INAPP = '站内提醒中心';
export const CHANNEL_INAPP_NOTE = '本页 · 始终可用，无需任何授权';
export const CHANNEL_INAPP_BADGE = '默认';
export const CHANNEL_WECHAT = '小程序订阅消息';
export const CHANNEL_WECHAT_NOTE = '本轮尚未接入（需小程序模板报备与真机授权）';
export const CHANNEL_WECHAT_BADGE = '未接入';

/** 「更早」被截断时必须说明（静默截断 = 让用户以为历史只有这么多） */
export function earlierTruncatedNote(limit: number): string {
  return `「更早」只显示最近 ${limit} 条（提醒按天累积，列表有界）。`;
}

// ─────────────────────────── 文本拼接（唯一的拼接处） ───────────────────────────

/** 金额文本（🔴 走共享层单一源；`null` ⇒ 不显示，**不显示 `¥0.00`**） */
export function amountTextOf(amount: Cents | null, currency: string): string | null {
  if (amount === null) return null;
  return formatMoneyText(amount, currency);
}

/** 相对到期文本（原型「8 天后还款」「5 天后到期」） */
export function dueTextOf(item: ReminderView): string | null {
  if (item.dueDate === null) return null;
  const d = item.daysUntil;
  if (d === null) return item.dueDate;
  if (d > 0) return `${d} 天后到期`;
  if (d === 0) return '今天到期';
  return `已逾期 ${-d} 天`;
}

/** 标题 —— 🔴 **唯一拼接处**（服务端只给 `subject` / `mergedCount` 等结构化字段） */
export function reminderTitle(item: ReminderView): string {
  const tail = item.tailNumber === null ? '' : `（尾号${item.tailNumber}）`;

  // 合并条：客户端把「N 笔」写出来（`AC-H1.9`；🔴 UI 一律「笔」，铁律 38）
  if (item.mergedCount > 1) {
    switch (item.type) {
      case ReminderType.Repayment:
        return `${item.mergedCount} 笔还款`;
      case ReminderType.Billing:
        return `${item.mergedCount} 张卡账单已出`;
      case ReminderType.Budget:
        return `${item.mergedCount} 个分类预算预警`;
      default:
        return `${item.mergedCount} 条提醒`;
    }
  }

  switch (item.type) {
    case ReminderType.Repayment: {
      const due = dueTextOf(item);
      return due === null ? `${item.subject}还款` : `${item.subject}${tail} · ${due}`;
    }
    case ReminderType.Billing:
      return `${item.subject}${tail}账单已出`;
    case ReminderType.Budget:
      return item.variant === 'over'
        ? `${item.subject}预算已超支`
        : `${item.subject}预算接近上限`;
    case ReminderType.Goal:
      return `${item.subject}目标进度`;
    case ReminderType.Habit:
      return `${item.subject}记账提醒`;
    default:
      return item.subject;
  }
}

/** 描述行（第二行 · 服务端只给数字） */
export function reminderDesc(item: ReminderView, currency: string): string | null {
  const money = amountTextOf(item.amount, currency);

  switch (item.type) {
    case ReminderType.Repayment:
      return money === null ? null : `应还 ${money}`;
    case ReminderType.Billing: {
      const parts: string[] = [];
      if (money !== null) parts.push(`本期应还 ${money}`);
      if (item.dueDate !== null) parts.push(`还款日 ${item.dueDate}`);
      return parts.length === 0 ? null : parts.join(' · ');
    }
    case ReminderType.Budget:
      return money === null ? null : `本期预算 ${money}`;
    default:
      return money === null ? null : money;
  }
}

/**
 * 「要做什么」的提示语（原型「立即还款」/「看账单明细」）。
 * `null` ⇒ 该条**不需要动作**（端侧据此显示「无需处理」）。
 *
 * ⚠️ 这是**提示**不是按钮文案：本批动作的**唯一可达终点 = 标记已处理**
 * （去还款 / 看账单明细需要对应页面，本批未接）⇒ 按钮文案走 `DONE_LABEL`。
 */
export function actionHintOf(item: ReminderView): string | null {
  return item.actionable ? ACTION_LABELS[item.type] : null;
}

/** 「更早」档里状态徽标的文案（`null` ⇒ 不显示） */
export function stateBadgeOf(item: ReminderView): string | null {
  return STATE_LABELS[item.state];
}
